import React from 'react';
import { FaBuilding, FaUsers, FaUserGraduate, FaChartLine, FaSync, FaExclamationTriangle } from 'react-icons/fa';
import { OrganizationData } from '../../services/databaseService';

interface DashboardStats {
  totalOrganizations: number;
  activeOrganizations: number;
  totalUsers: number;
  totalStudents: number;
}

interface OrganizationOverviewProps {
  stats: DashboardStats;
  organizations: OrganizationData[];
  onRefresh: () => void;
}

export const OrganizationOverview: React.FC<OrganizationOverviewProps> = ({
  stats,
  organizations,
  onRefresh
}) => {
  const recentOrganizations = organizations
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  const suspendedOrganizations = organizations.filter(org => org.status === 'suspended');
  const trialOrganizations = organizations.filter(org => org.status === 'trial');

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-500 text-sm font-medium">Total Organizaciones</div>
              <div className="text-3xl font-bold text-gray-900">{stats.totalOrganizations}</div>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <FaBuilding className="text-blue-600 w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-500 text-sm font-medium">Organizaciones Activas</div>
              <div className="text-3xl font-bold text-green-600">{stats.activeOrganizations}</div>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <FaChartLine className="text-green-600 w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-500 text-sm font-medium">Total Usuarios</div>
              <div className="text-3xl font-bold text-purple-600">{stats.totalUsers}</div>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <FaUsers className="text-purple-600 w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-500 text-sm font-medium">Total Estudiantes</div>
              <div className="text-3xl font-bold text-orange-600">{stats.totalStudents}</div>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <FaUserGraduate className="text-orange-600 w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Section */}
      {(suspendedOrganizations.length > 0 || trialOrganizations.length > 0) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <FaExclamationTriangle className="text-yellow-500 w-5 h-5 mr-2" />
              Alertas del Sistema
            </h3>
          </div>
          
          <div className="space-y-3">
            {suspendedOrganizations.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="text-red-600 font-medium">
                    {suspendedOrganizations.length} organización(es) suspendida(s)
                  </div>
                </div>
                <div className="mt-2 text-sm text-red-600">
                  {suspendedOrganizations.map(org => org.displayName).join(', ')}
                </div>
              </div>
            )}

            {trialOrganizations.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="text-yellow-600 font-medium">
                    {trialOrganizations.length} organización(es) en período de prueba
                  </div>
                </div>
                <div className="mt-2 text-sm text-yellow-600">
                  {trialOrganizations.map(org => org.displayName).join(', ')}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Organizations */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Organizaciones Recientes</h3>
          <button
            onClick={onRefresh}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors"
          >
            <FaSync className="w-4 h-4" />
            <span>Actualizar</span>
          </button>
        </div>

        {recentOrganizations.length > 0 ? (
          <div className="space-y-4">
            {recentOrganizations.map((org) => (
              <div key={org._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-100 p-2 rounded-full">
                    <FaBuilding className="text-blue-600 w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{org.displayName}</div>
                    <div className="text-sm text-gray-600">{org.tenantKey}</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className="text-sm text-gray-600">
                      {org.userCount || 0} usuarios • {org.studentCount || 0} estudiantes
                    </div>
                    <div className="text-xs text-gray-500">
                      Creada: {new Date(org.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    org.status === 'active' ? 'bg-green-100 text-green-800' :
                    org.status === 'trial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {org.status === 'active' ? 'Activa' : 
                     org.status === 'trial' ? 'Prueba' : 'Suspendida'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <FaBuilding className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600">No hay organizaciones registradas</p>
          </div>
        )}
      </div>

      {/* System Health */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Estado del Sistema</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {Math.round((stats.activeOrganizations / Math.max(stats.totalOrganizations, 1)) * 100)}%
            </div>
            <div className="text-sm text-green-600 font-medium">Organizaciones Activas</div>
          </div>
          
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {Math.round(stats.totalUsers / Math.max(stats.totalOrganizations, 1))}
            </div>
            <div className="text-sm text-blue-600 font-medium">Usuarios por Organización</div>
          </div>
          
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {Math.round(stats.totalStudents / Math.max(stats.totalOrganizations, 1))}
            </div>
            <div className="text-sm text-orange-600 font-medium">Estudiantes por Organización</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationOverview;